/**
 * Backward-compatible Tooltip component.
 *
 * New code should prefer:
 * - {@link TooltipTrigger} for inline label tooltips
 * - {@link TooltipStateBridge} for chart tooltips with isolation
 * - {@link TooltipPortal} for direct portal control
 *
 * This facade keeps the legacy API working:
 * - `inlineTooltip` / `titleTooltip` → delegates to TooltipTrigger
 * - children without inline → wraps with mouse-tracking + TooltipPortal
 * - no children → standalone TooltipPortal (caller manages state)
 */
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { borderRadius, dataVizLayout } from "@atlas-int/tokens";
import { TooltipPosition, TooltipTheme } from "./tooltip/SharedTooltipComponents";
import TooltipPortal from "./TooltipPortal";
import TooltipTrigger from "./TooltipTrigger";

// Re-exports preserved for existing barrel imports
export { TooltipPosition, TooltipTheme };
export * from "./tooltip/SharedTooltipComponents";
export * from "./tooltip/tooltipUtils";
export { TooltipLineMarker } from "./tooltip/TooltipLineMarker";

// ── Styled (hoisted) ────────────────────────────────────────────────

const GenericSpan = styled.span`
  cursor: help;
  display: contents;
`;

const Root = styled.span`
  cursor: help;
  width: ${dataVizLayout.tooltipTriggerSize};
  height: ${dataVizLayout.tooltipTriggerSize};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.pill};
  margin: ${dataVizLayout.tooltipTriggerMargin};
`;

// ── Types ────────────────────────────────────────────────────────────

interface Props {
  explanation: React.ReactNode | null;
  children?: React.ReactNode;
  cursor?: string;
  theme?: TooltipTheme;
  tooltipPosition?: TooltipPosition;
  overrideStyles?: boolean;
  delay?: number;
  title?: string | undefined;
  inlineTooltip?: boolean | undefined;
  titleTooltip?: boolean | undefined;
  isFixed?: boolean | undefined;
  isFixedCoords?: [number, number] | undefined;
  snapToPosition?: boolean | undefined;
  snapDirection?: "above" | "right" | undefined;
  clearHighlightedItem?: () => void | undefined;
  fillColor?: string | undefined;
  persistOnMouseLeave?: boolean | undefined;
  onFixedChange?: (isFixed: boolean) => void;
}

// ── Component ────────────────────────────────────────────────────────

const Tooltip = (props: Props) => {
  const {
    explanation,
    children,
    cursor,
    theme,
    tooltipPosition,
    overrideStyles,
    delay,
    inlineTooltip,
    titleTooltip,
    isFixed,
    isFixedCoords,
    snapToPosition,
    snapDirection,
    clearHighlightedItem,
    persistOnMouseLeave,
    onFixedChange,
  } = props;

  // ── Inline / title tooltip → delegate ────────────────────────────
  if (inlineTooltip || titleTooltip) {
    return (
      <TooltipTrigger
        explanation={explanation}
        inline={inlineTooltip ?? false}
        italic={titleTooltip ?? false}
        cursor={cursor}
        delay={delay}
        theme={theme}
        tooltipPosition={tooltipPosition}
      >
        {children}
      </TooltipTrigger>
    );
  }

  // ── Chart / portal tooltip → hover + fixed state management ──────
  return (
    <ChartTooltipCompat
      explanation={explanation}
      cursor={cursor}
      theme={theme}
      tooltipPosition={tooltipPosition}
      overrideStyles={overrideStyles}
      isFixed={isFixed}
      isFixedCoords={isFixedCoords}
      snapToPosition={snapToPosition}
      snapDirection={snapDirection}
      clearHighlightedItem={clearHighlightedItem}
      persistOnMouseLeave={persistOnMouseLeave}
      onFixedChange={onFixedChange}
      delay={delay}
    >
      {children}
    </ChartTooltipCompat>
  );
};

export default memo(Tooltip);

// ── Internal: chart/portal tooltip with hover + fixed state ─────────

interface ChartTooltipCompatProps {
  explanation: React.ReactNode | null;
  children?: React.ReactNode;
  cursor?: string;
  theme?: TooltipTheme;
  tooltipPosition?: TooltipPosition;
  overrideStyles?: boolean;
  isFixed?: boolean;
  isFixedCoords?: [number, number];
  snapToPosition?: boolean;
  snapDirection?: "above" | "right";
  clearHighlightedItem?: () => void;
  persistOnMouseLeave?: boolean;
  onFixedChange?: (isFixed: boolean) => void;
  delay?: number;
}

const ChartTooltipCompat = memo(function ChartTooltipCompat({
  explanation,
  children,
  cursor,
  theme,
  tooltipPosition,
  overrideStyles,
  isFixed: isFixedProp,
  isFixedCoords,
  snapToPosition,
  snapDirection,
  clearHighlightedItem,
  persistOnMouseLeave,
  onFixedChange,
  delay,
}: ChartTooltipCompatProps) {
  const rootEl = useRef<HTMLSpanElement | null>(null);
  const timeoutRef = useRef<number | undefined>(undefined);

  const [isTooltipShown, setIsTooltipShown] = useState(
    isFixedProp === true,
  );
  const [mouseCoords, setMouseCoords] = useState({ top: 0, left: 0 });
  const [isTooltipFixed, setIsTooltipFixed] = useState(
    isFixedProp ?? false,
  );

  const clearTooltipAndHighlights = useCallback(() => {
    setIsTooltipFixed(false);
    setIsTooltipShown(false);
    clearHighlightedItem?.();
  }, [clearHighlightedItem]);

  // Sync controlled isFixed prop → internal state
  useEffect(() => {
    if (isFixedProp === true) {
      setIsTooltipShown(true);
      setIsTooltipFixed(true);
    } else if (isFixedProp === false) {
      setIsTooltipFixed(false);
    }
  }, [isFixedProp]);

  useEffect(() => {
    onFixedChange?.(isTooltipFixed);
  }, [isTooltipFixed, onFixedChange]);

  // Compute portal coords:
  //  - isFixedCoords provided → use those (snap-to-point or pinned)
  //  - otherwise → use mouse-tracked coords
  const coords =
    isFixedCoords !== undefined
      ? { top: isFixedCoords[1], left: isFixedCoords[0] }
      : mouseCoords;

  const portal =
    isTooltipShown && explanation ? (
      <TooltipPortal
        explanation={explanation}
        coords={coords}
        isFixed={isTooltipFixed}
        theme={theme}
        tooltipPosition={tooltipPosition}
        overrideStyles={overrideStyles}
        snapToPosition={snapToPosition}
        snapDirection={snapDirection}
        onClose={clearTooltipAndHighlights}
      />
    ) : null;

  // ── Event handlers ───────────────────────────────────────────────

  const onMouseEnter = (e: React.MouseEvent) => {
    if (!isTooltipFixed) {
      setMouseCoords({ top: e.clientY, left: e.clientX });
      if (delay) {
        timeoutRef.current = setTimeout(
          () => setIsTooltipShown(true),
          delay,
        ) as unknown as number;
      } else {
        setIsTooltipShown(true);
      }
    }
  };

  const onMouseLeave = (e: React.MouseEvent) => {
    if (!isTooltipFixed) {
      setMouseCoords({ top: e.clientY, left: e.clientX });
      clearTimeout(timeoutRef.current);
      if (persistOnMouseLeave) {
        setIsTooltipShown(false);
      } else {
        clearTooltipAndHighlights();
      }
    } else {
      e.stopPropagation();
    }
  };

  /*
   * onMouseMoveCapture: capture-phase handler so that when the tooltip is
   * fixed, child mousemove events are intercepted before they can update
   * tooltip content.
   */
  const onMouseMoveCapture = (e: React.MouseEvent) => {
    if (!isTooltipShown && explanation) setIsTooltipShown(true);
    if (!isTooltipFixed && isFixedCoords === undefined) {
      setMouseCoords({ top: e.clientY, left: e.clientX });
    } else if (isTooltipFixed) {
      e.stopPropagation();
    }
  };

  const onMouseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isTooltipFixed || isFixedProp) {
      clearTooltipAndHighlights();
    } else if (explanation && !isTooltipFixed) {
      setIsTooltipFixed(true);
    }
    if (isTooltipShown) {
      clearHighlightedItem?.();
    }
  };

  // ── Render ───────────────────────────────────────────────────────

  if (children !== undefined) {
    return (
      <GenericSpan
        onMouseEnter={onMouseEnter}
        onMouseMoveCapture={onMouseMoveCapture}
        onMouseLeave={onMouseLeave}
        onClick={onMouseClick}
        ref={rootEl}
        style={{ cursor }}
      >
        {children}
        {portal}
      </GenericSpan>
    );
  }

  return (
    <Root
      onMouseEnter={onMouseEnter}
      onMouseMoveCapture={onMouseMoveCapture}
      onMouseLeave={onMouseLeave}
      style={{ cursor }}
      ref={rootEl}
    >
      {portal}
    </Root>
  );
});
